import React from 'react';
import { observer } from 'mobx-react';
import {
    Grid,
    Box,
    Tooltip,
} from '@material-ui/core';
import Button from '../../../../commons/ButtonProgress';
import PlanSummaryHeader from '../../../../components/Billing/PlanSummaryHeader';
import PlanScheduleBanner from '../../../../components/Billing/PlanScheduleBanner';
import UsageBar from '../../../../components/Billing/UsageBar';
import { getPlanScheduleNotice, formatShortPlanDate, formatCurrentMonthYear } from './planScheduleNotice';
import useConnectPlanState from './useConnectPlanState';
import useUpgradePlanDialog from './dialogs/upgradePlanDialog/useUpgradePlanDialog';
import useCancelConnectPlanDialog from './dialogs/cancelConnectPlanDialog/useCancelConnectPlanDialog';
import usePurchaseTransactionsDialog from './dialogs/purchaseTransactionsDialog/usePurchaseTransactionsDialog';

import { useStyles } from './styles';

const CHANGE_PLAN_DISABLED_TOOLTIP = 'A plan change is scheduled already.';

function ConnectPlanCard () {
    const { planRevision, plan, usage, plansResolved } = useConnectPlanState();

    const { renderUpgradePlanDialog, openDialog } = useUpgradePlanDialog();
    const {
        renderCancelConnectPlanDialog,
        openDialog: openCancelDialog,
    } = useCancelConnectPlanDialog({
        planName: plan ? `Connect · ${plan.planName} plan` : '',
        pricePerMonth: plan ? plan.priceMonthly : 0,
    });
    const { renderPurchaseTransactionsDialog, openDialog: openPurchaseDialog } =
        usePurchaseTransactionsDialog({ usage });

    const classes = useStyles();

    // Null state — no Connect plan subscribed, render nothing.
    if (!plan) {
        return null;
    }

    const {
        planName,
        transactionLimit,
        priceMonthly,
        status,
    } = plan;

    const { used, total } = usage;

    const remaining = total - used;
    const usedPercent = total > 0 ? Math.min((used / total) * 100, 100) : 0;
    const isCancellationPending = status === 'pending_cancellation';
    const isChangeScheduled = status === 'pending_upgrade' || status === 'pending_downgrade';

    const scheduleNotice = getPlanScheduleNotice(plan);
    const resetLabel = plan.nextRenewalDate ? `Resets ${formatShortPlanDate(plan.nextRenewalDate)}` : '';

    return (
        <React.Fragment>
            <Grid key={planRevision} item justify={'center'} container>
                <Box
                    display={'flex'}
                    flexDirection={'column'}
                    className={classes.card}
                >
                    <PlanSummaryHeader
                        microLabel="Connect Plan"
                        planName={planName}
                        showActiveBadge={status === 'active' || isChangeScheduled}
                        showCancellationPendingBadge={isCancellationPending}
                        tierLabel={`${transactionLimit.toLocaleString()} transactions per month`}
                        price={priceMonthly}
                        currencySymbol="$"
                    />

                    {scheduleNotice && (
                        <PlanScheduleBanner tone={scheduleNotice.tone}>
                            {scheduleNotice.tone === 'cancellation' ? (
                                <React.Fragment>Your Connect subscription will be cancelled on <strong>{scheduleNotice.date}</strong>.</React.Fragment>
                            ) : (
                                <React.Fragment>Your Connect plan will {scheduleNotice.tone} to <strong>{scheduleNotice.nextPlanName}</strong> on <strong>{scheduleNotice.date}</strong>.</React.Fragment>
                            )}
                        </PlanScheduleBanner>
                    )}

                    <UsageBar
                        label={`Transaction usage · ${formatCurrentMonthYear()}`}
                        used={used}
                        total={total}
                        remaining={remaining}
                        usedPercent={usedPercent}
                        resetLabel={resetLabel}
                    />

                    {/* Actions */}
                    <div className={classes.actionsRow}>
                        <Button
                            variant={'contained'}
                            className={classes.purchaseBtn}
                            onClick={openPurchaseDialog}
                            disabled={!plansResolved}
                            label='+ Purchase Transactions'
                        >
                        </Button>
                        {!isCancellationPending && (
                            <React.Fragment>
                                <Tooltip
                                    title={isChangeScheduled ? CHANGE_PLAN_DISABLED_TOOLTIP : ''}
                                    placement="top"
                                    classes={{ tooltip: classes.tooltip }}
                                >
                                    <span>
                                        <Button
                                            disabled={isChangeScheduled}
                                            variant={'outlined'}
                                            className={classes.upgradeBtn}
                                            onClick={openDialog}
                                            label='Change Plan'
                                        >
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Button
                                    variant={'outlined'}
                                    className={classes.cancelBtn}
                                    onClick={openCancelDialog}
                                    label='Cancel'
                                >
                                </Button>
                            </React.Fragment>
                        )}
                    </div>
                </Box>
            </Grid>

            {renderUpgradePlanDialog()}
            {renderCancelConnectPlanDialog()}
            {renderPurchaseTransactionsDialog()}
        </React.Fragment>
    );
}

const ObservedConnectPlanCard = observer(ConnectPlanCard);
export default ObservedConnectPlanCard;
